import { useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Card } from '@/shared/components/ui/Card';
import { api } from '@/shared/api/axios';
import { useLeaveTypes, useLeaveBalances, useHolidays } from '../hooks/useHr';
import { LEAVE_TYPE_OPTIONS, OPTIONAL_HOLIDAY_CODE, fmtDate } from '../constants';
import type { SubmitLeavePayload } from '../types';

interface SubmitLeaveModalProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: SubmitLeavePayload) => void;
}

function defaultLeaveDateISO() {
  const date = new Date();
  // A leave request opened on a weekend previously defaulted to that weekend,
  // so its first submit failed with a confusing "No working days" message.
  // Start on the next weekday instead; holiday validation remains server-side.
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }
  return date.toISOString().slice(0, 10);
}

interface FormState {
  type: string;
  from_date: string;
  to_date: string;
  reason: string;
  is_half_day: boolean;
  half_day_session: 'first_half' | 'second_half';
  document_url: string;
}

export function SubmitLeaveModal({ open, loading, onClose, onSubmit }: SubmitLeaveModalProps) {
  const { data: leaveTypes = [] } = useLeaveTypes();
  const { data: balanceData } = useLeaveBalances();
  const balances = balanceData?.balances ?? [];
  const thisYear = new Date().getFullYear();
  const { data: holidaysThisYear = [] } = useHolidays(thisYear);
  const { data: holidaysNextYear = [] } = useHolidays(thisYear + 1);

  const [form, setForm] = useState<FormState>({
    type: '',
    from_date: defaultLeaveDateISO(),
    to_date: defaultLeaveDateISO(),
    reason: '',
    is_half_day: false,
    half_day_session: 'first_half',
    document_url: '',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  // Reset form on open
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      const defaultType = leaveTypes.length > 0 ? leaveTypes[0].code : 'annual';
      setForm({
        type: defaultType,
        from_date: defaultLeaveDateISO(),
        to_date: defaultLeaveDateISO(),
        reason: '',
        is_half_day: false,
        half_day_session: 'first_half',
        document_url: '',
      });
      setUploadedFileName('');
    }
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Build type options from dynamic types (fallback to legacy)
  const typeOptions = useMemo(() => {
    if (leaveTypes.length > 0) {
      return leaveTypes.map((lt) => ({ value: lt.code, label: lt.name }));
    }
    return LEAVE_TYPE_OPTIONS;
  }, [leaveTypes]);

  // Current leave type config
  const selectedType = leaveTypes.find((lt) => lt.code === form.type);

  // Balance for selected type
  const selectedBalance = balances.find((b) => b.leave_type_code === form.type);
  const availableBalance = selectedBalance?.available ?? null;

  // Optional holiday: the employee picks one of the optional days on the calendar instead of a date range.
  const isOptionalHoliday = form.type === OPTIONAL_HOLIDAY_CODE;
  const optionalHolidays = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...holidaysThisYear, ...holidaysNextYear]
      .filter((h) => h.is_optional && h.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [holidaysThisYear, holidaysNextYear]);

  const pickOptionalHoliday = (date: string) => setForm((prev) => ({ ...prev, from_date: date, to_date: date, is_half_day: false }));

  // Is single-day? (for half-day toggle)
  const isSingleDay = form.from_date === form.to_date;

  // Validation
  const invalidRange = new Date(form.to_date) < new Date(form.from_date);
  const noHolidayPicked = isOptionalHoliday && !optionalHolidays.some((h) => h.date === form.from_date);

  // Check if document is required
  const estimatedDays = form.is_half_day ? 0.5 : Math.max(1, Math.round((new Date(form.to_date).getTime() - new Date(form.from_date).getTime()) / 86400000) + 1);
  const docRequired = selectedType?.requires_document && estimatedDays >= (selectedType.document_threshold_days || 1);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post<{ url: string }>('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('document_url', data.url);
      setUploadedFileName(file.name);
    } catch {
      // File upload failed silently
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (invalidRange || noHolidayPicked) return;
    if (docRequired && !form.document_url) return;

    const payload: SubmitLeavePayload = {
      type: form.type,
      from_date: form.from_date,
      to_date: form.to_date,
      reason: form.reason.trim(),
    };
    if (form.is_half_day && isSingleDay) {
      payload.is_half_day = true;
      payload.half_day_session = form.half_day_session;
    }
    if (form.document_url) {
      payload.document_url = form.document_url;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Submit Leave Request"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="submit-leave-form"
            loading={loading}
            disabled={invalidRange || noHolidayPicked || (docRequired && !form.document_url)}
          >
            Submit Request
          </Button>
        </>
      }
    >
      <form id="submit-leave-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Leave Type */}
        <Select
          label="Leave Type"
          name="leave-type"
          value={form.type}
          onChange={(e) => set('type', e.target.value)}
          options={typeOptions}
        />


        {/* Balance indicator */}
        {selectedType?.is_paid && availableBalance !== null && (
          <Card className="flex items-center justify-between px-3 py-2">
            <span className="text-body-sm text-on-surface-variant">Available balance</span>
            <span className={`text-data-md font-semibold ${availableBalance <= 0 ? 'text-danger' : 'text-success'}`}>
              {availableBalance} day{availableBalance !== 1 ? 's' : ''}
            </span>
          </Card>
        )}

        {isOptionalHoliday ? (
          optionalHolidays.length === 0 ? (
            <p className="text-body-sm text-on-surface-variant">There are no optional holidays coming up on the calendar.</p>
          ) : (
            <Select
              label="Optional holiday *"
              name="optional-holiday"
              value={optionalHolidays.some((h) => h.date === form.from_date) ? form.from_date : ''}
              onChange={(e) => pickOptionalHoliday(e.target.value)}
              options={[{ value: '', label: 'Choose a holiday' }, ...optionalHolidays.map((h) => ({ value: h.date, label: `${fmtDate(h.date)} - ${h.name}` }))]}
            />
          )
        ) : (
          <>
        {/* Date range */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="From *"
            name="leave-from"
            type="date"
            value={form.from_date}
            onChange={(e) => set('from_date', e.target.value)}
            required
          />
          <Input
            label="To *"
            name="leave-to"
            type="date"
            value={form.to_date}
            onChange={(e) => set('to_date', e.target.value)}
            error={invalidRange ? 'End date must be on/after start date' : undefined}
            required
          />
        </div>

        {/* Half-day toggle — only for single-day */}
        {isSingleDay && (
          <div className="flex items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_half_day}
                onChange={(e) => set('is_half_day', e.target.checked)}
                className="h-4 w-4 rounded border-outline text-primary focus:ring-primary/40"
              />
              <span className="text-body-sm text-on-surface">Half-day leave</span>
            </label>
            {form.is_half_day && (
              <Select
                name="half-day-session"
                value={form.half_day_session}
                onChange={(e) => set('half_day_session', e.target.value as 'first_half' | 'second_half')}
                options={[
                  { value: 'first_half', label: 'First Half' },
                  { value: 'second_half', label: 'Second Half' },
                ]}
              />
            )}
          </div>
        )}
          </>
        )}

        {/* Reason */}
        <div>
          <label htmlFor="leave-reason" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Reason
          </label>
          <textarea
            id="leave-reason"
            rows={3}
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
            placeholder="Brief reason for leave…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Document upload */}
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Supporting Document
            {docRequired && <span className="ml-1 text-danger">*</span>}
          </label>
          {docRequired && (
            <p className="mb-2 text-[11px] text-on-surface-variant">
              A document is required for {selectedType?.name} leaves of {selectedType?.document_threshold_days || 1}+ day(s).
            </p>
          )}
          <label className="flex cursor-pointer items-center gap-2 rounded-DEFAULT border border-dashed border-outline px-3 py-2.5 transition-colors hover:border-primary hover:bg-surface-variant/30">
            <Upload className="h-4 w-4 text-on-surface-variant" />
            <span className="text-body-sm text-on-surface-variant">
              {uploading ? 'Uploading…' : uploadedFileName || 'Choose file to upload'}
            </span>
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </label>
          {form.document_url && (
            <p className="mt-1 text-[11px] text-success">Document uploaded successfully.</p>
          )}
        </div>
      </form>
    </Modal>
  );
}
